import { useId } from "react";
import { useTranslations } from "use-intl";
import dayjs from "dayjs";
import { Alert, Divider, Skeleton } from "@mui/material";
import type { TripDetails } from "@/app/lib/types/trip";
import { Image } from "@/app/ui/Image";
import { DetailsList, DetailsListItem } from "../../DetailsList";

type Props = {
  trip: TripDetails;
  startDate: string | undefined;
};

export function OverviewCard({ trip, startDate }: Props) {
  const t = useTranslations();
  const alertTitleId = useId();

  const startDateFormatted = dayjs(startDate).format("D MMMM, YYYY");
  const endDateFormatted = dayjs(startDate)
    .add(trip.durationInDays, "day")
    .format("D MMMM, YYYY");

  return (
    <div className="rounded-3xl bg-white p-1">
      <Image
        src={trip.imageUrls[0]}
        alt={trip.name}
        className="aspect-7/2 w-full rounded-t-2xl object-cover"
      />

      <div className="p-3">
        <div className="mb-1 text-xl font-medium text-text-title">
          {trip.name}
        </div>
        <div className="text-sm text-text-tertiary">{trip.subtitle}</div>

        <Divider
          sx={{
            borderColor: "var(--color-stroke-neutral-soft)",
            my: "calc(var(--spacing)*3)",
          }}
        />

        <DetailsList>
          <DetailsListItem
            title={t("trips.travelStartDate")}
            value={
              startDate ? (
                startDateFormatted
              ) : (
                <Skeleton>
                  <span>{startDateFormatted}</span>
                </Skeleton>
              )
            }
          />

          <DetailsListItem
            title={t("trips.travelEndDate")}
            value={
              startDate ? (
                endDateFormatted
              ) : (
                <Skeleton>
                  <span>{endDateFormatted}</span>
                </Skeleton>
              )
            }
          />
        </DetailsList>

        <Alert
          severity="info"
          sx={{
            mt: "calc(var(--spacing)*4)",
          }}
          aria-labelledby={alertTitleId}
        >
          <div
            className="mb-1 text-sm font-medium text-black"
            id={alertTitleId}
          >
            {t("trips.bookingInfo.title")}
          </div>
          <div className="text-sm text-text-placeholder">
            {t("trips.bookingInfo.content")}
          </div>
        </Alert>
      </div>
    </div>
  );
}
